"use client";

import { CheckIcon, LoaderCircleIcon, WalletIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { useStatus } from "@/hooks/useStatus";
import { isUserRejection, useWatchAsset, useWatchAssetSupported, type WatchAssetParams } from "@/hooks/useWatchAsset";
import { errorMessage } from "@/lib/format";
import { cn } from "@/lib/utils";

const ADDED_FEEDBACK_MS = 2000;

interface AddToWalletButtonProps {
  /**
   * The asset to hand to wallet_watchAsset. Accepts a thunk because the LP
   * call sites can't build the params synchronously: wallets validate the
   * supplied symbol/decimals against the token contract and reject on
   * mismatch, so those have to be read on-chain first — and only when the
   * button is actually pressed, not for every row on render.
   */
  asset: WatchAssetParams | (() => Promise<WatchAssetParams>);
  /** Asset name for the tooltip, screen readers and the success toast, e.g. "USDC" or "DOTO/USDC LP". */
  assetLabel: string;
  /**
   * "row" for list rows (Tokens/Pools/Positions), "inline" for the
   * post-transaction footers that sit beside a "View transaction ↗" link and
   * need to match that link's scale rather than tower over it.
   */
  density?: "row" | "inline";
  /** Suppress the visible label where horizontal space is tight; tooltip and aria-label stay. */
  iconOnly?: boolean;
  className?: string;
}

/**
 * The single "Add to wallet" affordance for every asset type — plain ERC20s,
 * V2 LP tokens and V3 position NFTs. Previously six call sites had grown four
 * different visual treatments and three different labels between them, only
 * two showed that a click had registered, and one swallowed its errors.
 *
 * One icon and one label for all three, because the action is identical
 * regardless of what's being added; what the asset *is* already reads from the
 * surrounding row, so the specificity lives in the tooltip/aria-label instead
 * of the visible text. Ghost variant keeps it from competing with the real CTA
 * next to it (PoolItem's "Add liquidity"), and the idle → adding → added
 * states matter because the wallet's own confirmation prompt is modal and slow
 * enough that a silent button reads as broken.
 */
export function AddToWalletButton({
  asset,
  assetLabel,
  density = "row",
  iconOnly = false,
  className,
}: AddToWalletButtonProps) {
  const watchAsset = useWatchAsset();
  const supported = useWatchAssetSupported();
  const { showStatus } = useStatus();
  const [state, setState] = useState<"idle" | "adding" | "added">("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => void (resetTimer.current && clearTimeout(resetTimer.current)), []);

  // Hidden rather than disabled when there's no wallet: the hook is false
  // whenever no connector is attached, so this is also the single gate for
  // "not connected" — call sites don't need their own isConnected check.
  if (!supported) return null;

  async function addToWallet() {
    setState("adding");
    try {
      await watchAsset(typeof asset === "function" ? await asset() : asset);
      setState("added");
      showStatus(`${assetLabel} added to your wallet`, "success");
      resetTimer.current = setTimeout(() => setState("idle"), ADDED_FEEDBACK_MS);
    } catch (e) {
      setState("idle");
      // Declining the wallet prompt is a choice, not a failure — stay quiet.
      if (!isUserRejection(e)) showStatus(errorMessage(e, `Couldn't add ${assetLabel} to your wallet`), "error");
    }
  }

  const label = state === "adding" ? "Adding…" : state === "added" ? "Added" : "Add to wallet";
  const title =
    state === "idle" ? `Add ${assetLabel} to your wallet` : state === "adding" ? `Adding ${assetLabel}…` : `${assetLabel} added`;

  return (
    <Button
      variant="ghost"
      size={iconOnly ? (density === "inline" ? "icon-xs" : "icon-sm") : density === "inline" ? "xs" : "sm"}
      className={cn("border border-border text-muted-foreground hover:text-foreground", className)}
      title={title}
      aria-label={title}
      disabled={state !== "idle"}
      onClick={addToWallet}
    >
      {state === "adding" ? (
        <LoaderCircleIcon className="animate-spin" />
      ) : state === "added" ? (
        <CheckIcon />
      ) : (
        <WalletIcon />
      )}
      {!iconOnly && label}
    </Button>
  );
}
